/**
 * ReconciliationDetailPanel
 * Panel de detalle para jobs de tipo BookingReconciliation.
 * Recibe el resultado completo por prop desde JobDetailDrawer — cero re-fetch.
 * Usa DataGridPro virtualizado con quick filter y sort nativo.
 */
import { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Alert,
  Chip,
  MenuItem,
  Pagination,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  DataGridPro,
  GridToolbarQuickFilter,
  GridToolbarContainer,
} from "@mui/x-data-grid-pro";
import type { GridColDef } from "@mui/x-data-grid-pro";
import {
  CheckCircleOutlined,
  WarningAmberOutlined,
  LinkOffOutlined,
  InfoOutlined,
} from "@mui/icons-material";
import type { BookingReconciliationResultDto, ReconciliationMatchDetailDto, UnmatchedBookingDto } from "@/types/jobs";
import { EquipmentLinksSummary } from "@/components/bookings/reconciliation/EquipmentLinksSummary";
import { STAT_NUMBER_SX, SECTION_LABEL_SX, CHIP_GEOMETRIC_SX } from "../jobs-design-tokens";

// ─── Match level metadata ─────────────────────────────────────────────────────

const LEVEL_META: Record<number, { label: string; desc: string; color: string }> = {
  1: { label: "L1", color: "#1D4ED8", desc: "Match exacto por referencia de booking" },
  2: { label: "L2", color: "#15803D", desc: "Match por MBL o referencia similar" },
  3: { label: "L3", color: "#d97706", desc: "Match heurístico por puertos, fechas y naviera" },
};

// ─── Failure reason metadata ──────────────────────────────────────────────────

const FAILURE_META: Record<string, { label: string; desc: string; color: string }> = {
  NoL3Candidates: { label: "Sin candidatos", color: "#dc2626", desc: "Este booking de naviera no encontró ningún booking en el sistema que coincida." },
  NoL2Candidates: { label: "Sin candidatos L2", color: "#dc2626", desc: "No se encontró match por MBL ni referencia similar." },
  NoL1Candidates: { label: "Sin match exacto", color: "#f97316", desc: "No hay match por referencia exacta. El algoritmo intentará niveles superiores." },
  MultipleCandidates: { label: "Múltiples candidatos", color: "#d97706", desc: "Se encontraron varios bookings candidatos y no se pudo determinar cuál es el correcto." },
  AmbiguousMatch: { label: "Match ambiguo", color: "#d97706", desc: "El match encontrado no es suficientemente confiable. Revisión manual requerida." },
};

function read<T>(source: unknown, ...keys: string[]): T | undefined {
  const record = source as Record<string, unknown> | null | undefined;
  if (!record) return undefined;
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null) return value as T;
  }
  return undefined;
}

function normalizeMatch(match: unknown): ReconciliationMatchDetailDto {
  return {
    NavieraBookingId: read<number>(match, "NavieraBookingId", "navieraBookingId") ?? 0,
    NavieraBookingReference: read<string | null>(match, "NavieraBookingReference", "navieraBookingReference") ?? null,
    RepoDocBookingId: read<number>(match, "RepoDocBookingId", "repoDocBookingId") ?? 0,
    RepoDocBookingReference: read<string | null>(match, "RepoDocBookingReference", "repoDocBookingReference") ?? null,
    MatchLevel: read<number>(match, "MatchLevel", "matchLevel") ?? 0,
    EquipmentLinks: read(match, "EquipmentLinks", "equipmentLinks") ?? [],
  };
}

function normalizeUnmatched(booking: unknown): UnmatchedBookingDto {
  return {
    BookingId: read<number>(booking, "BookingId", "bookingId") ?? 0,
    BookingReference: read<string | null>(booking, "BookingReference", "bookingReference") ?? null,
    Source: read<string | null>(booking, "Source", "source") ?? null,
    FailureReason: read<string | null>(booking, "FailureReason", "failureReason") ?? null,
  };
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, sublabel, color }: {
  icon: React.ReactNode; label: string; value: number; sublabel: string; color: string;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.25,
        py: 1.5,
        px: 0.5,
        borderRadius: 2,
        bgcolor: `${color}0a`,
        border: "1px solid",
        borderColor: `${color}20`,
        transition: "box-shadow 0.15s",
        "&:hover": { boxShadow: `0 2px 8px ${color}15` },
      }}
    >
      <Box sx={{ color, display: "flex", mb: 0.25 }}>{icon}</Box>
      <Typography sx={{ ...STAT_NUMBER_SX, color, fontSize: "24px" }}>{value}</Typography>
      <Typography sx={{ ...SECTION_LABEL_SX, color: "text.primary", lineHeight: 1.2, textAlign: "center" }}>{label}</Typography>
      <Typography variant="caption" color="text.disabled" lineHeight={1.2} textAlign="center" sx={{ fontSize: "0.63rem" }}>{sublabel}</Typography>
    </Box>
  );
}

// ─── Quick filter toolbar ─────────────────────────────────────────────────────

function QuickFilterToolbar() {
  return (
    <GridToolbarContainer sx={{ px: 1, py: 0.5 }}>
      <GridToolbarQuickFilter
        variant="outlined"
        size="small"
        sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.8rem", height: 30, borderRadius: "6px" } }}
        debounceMs={200}
      />
    </GridToolbarContainer>
  );
}

// ─── Column defs ──────────────────────────────────────────────────────────────

const matchColumns: GridColDef<ReconciliationMatchDetailDto>[] = [
  {
    field: "RepoDocBookingReference",
    headerName: "Booking sistema",
    flex: 1.2,
    renderCell: ({ row }) => (
      <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "12px", fontWeight: 600 }}>
        {row.RepoDocBookingReference ?? `#${row.RepoDocBookingId}`}
      </Typography>
    ),
  },
  {
    field: "NavieraBookingReference",
    headerName: "Booking naviera",
    flex: 1.2,
    renderCell: ({ row }) => (
      <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "12px", color: "text.secondary" }}>
        {row.NavieraBookingReference ?? `#${row.NavieraBookingId}`}
      </Typography>
    ),
  },
  {
    field: "MatchLevel",
    headerName: "Nivel",
    width: 90,
    renderCell: ({ value }) => {
      const meta = LEVEL_META[value as number];
      const color = meta?.color ?? "#6b7280";
      return (
        <Tooltip title={meta ? `${meta.label}: ${meta.desc}` : `Nivel ${value}`} placement="left" arrow>
          <Chip
            label={`L${value}`}
            size="small"
            variant="outlined"
            sx={{ height: 18, fontSize: "0.62rem", color, borderColor: color, bgcolor: `${color}10`, cursor: "help", ...CHIP_GEOMETRIC_SX }}
          />
        </Tooltip>
      );
    },
  },
];

const unmatchedColumns: GridColDef<UnmatchedBookingDto>[] = [
  {
    field: "Source",
    headerName: "Origen",
    width: 100,
    renderCell: ({ value }) =>
      value ? (
        <Chip
          label={value}
          size="small"
          variant="outlined"
          sx={{ height: 15, fontSize: "0.58rem", color: value === "NAVIERA" ? "#7c3aed" : "text.secondary", borderColor: value === "NAVIERA" ? "#7c3aed40" : "divider", ...CHIP_GEOMETRIC_SX }}
        />
      ) : null,
  },
  {
    field: "BookingReference",
    headerName: "Referencia",
    flex: 1.2,
    renderCell: ({ row }) => (
      <Typography variant="caption" fontWeight={600} sx={{ fontFamily: "monospace", fontSize: "12px" }}>
        {row.BookingReference ?? `#${row.BookingId}`}
      </Typography>
    ),
  },
  {
    field: "FailureReason",
    headerName: "Motivo",
    flex: 1.5,
    renderCell: ({ value }) => {
      if (!value) return null;
      const meta = FAILURE_META[value as string];
      const color = meta?.color ?? "#dc2626";
      return (
        <Tooltip title={meta?.desc ?? value} placement="bottom-start" arrow>
          <Chip
            label={meta?.label ?? value}
            size="small"
            variant="outlined"
            sx={{ height: 17, fontSize: "0.6rem", color, borderColor: color, bgcolor: `${color}08`, cursor: "help", ...CHIP_GEOMETRIC_SX }}
          />
        </Tooltip>
      );
    },
  },
];

function getMatchEquipmentLinks(match: ReconciliationMatchDetailDto) {
  return match.EquipmentLinks ?? match.equipmentLinks ?? [];
}

function buildEquipmentSearchText(match: ReconciliationMatchDetailDto): string {
  const values: unknown[] = [
    match.NavieraBookingId,
    match.NavieraBookingReference,
    match.RepoDocBookingId,
    match.RepoDocBookingReference,
    match.MatchLevel,
  ];

  for (const link of getMatchEquipmentLinks(match)) {
    const record = link as unknown as Record<string, unknown>;
    values.push(
      record.id,
      record.Id,
      record.containerId,
      record.ContainerId,
      record.navieraBookingEquipmentId,
      record.NavieraBookingEquipmentId,
      record.repoDocBookingEquipmentId,
      record.RepoDocBookingEquipmentId,
      record.navieraContainerId,
      record.NavieraContainerId,
      record.repoDocContainerId,
      record.RepoDocContainerId,
      record.navieraEquipmentCode,
      record.NavieraEquipmentCode,
      record.repoDocEquipmentCode,
      record.RepoDocEquipmentCode
    );
  }

  return values
    .filter((value) => value !== undefined && value !== null)
    .join(" ")
    .toLowerCase();
}

// ─── Panel principal ──────────────────────────────────────────────────────────

interface Props { result: BookingReconciliationResultDto; }

export function ReconciliationDetailPanel({ result }: Props) {
  const [tab, setTab] = useState(0);
  const [equipmentQuery, setEquipmentQuery] = useState("");
  const [equipmentPage, setEquipmentPage] = useState(1);
  const [equipmentPageSize, setEquipmentPageSize] = useState(25);

  const matched = read<number>(result, "TotalMatched", "totalMatched") ?? 0;
  const pending = read<number>(result, "TotalPendingReview", "totalPendingReview") ?? 0;
  const unmatched = read<number>(result, "TotalUnmatched", "totalUnmatched") ?? 0;
  const total = matched + pending + unmatched;
  const pct = total > 0 ? Math.round((matched / total) * 100) : 0;

  const matchedList = (read<unknown[]>(result, "Matches", "matches") ?? []).map(normalizeMatch);
  const pendingList = (read<unknown[]>(result, "PendingReviewBookings", "pendingReviewBookings") ?? []).map(normalizeMatch);
  const unmatchedList = (read<unknown[]>(result, "UnmatchedBookings", "unmatchedBookings") ?? []).map(normalizeUnmatched);
  const matchesWithEquipmentLinks = matchedList.filter((match) => getMatchEquipmentLinks(match).length > 0);
  const normalizedEquipmentQuery = equipmentQuery.trim().toLowerCase();
  const filteredEquipmentMatches = useMemo(
    () =>
      normalizedEquipmentQuery
        ? matchesWithEquipmentLinks.filter((match) => buildEquipmentSearchText(match).includes(normalizedEquipmentQuery))
        : matchesWithEquipmentLinks,
    [matchesWithEquipmentLinks, normalizedEquipmentQuery]
  );
  const equipmentPageCount = Math.max(1, Math.ceil(filteredEquipmentMatches.length / equipmentPageSize));
  const safeEquipmentPage = Math.min(equipmentPage, equipmentPageCount);
  const equipmentRangeStart = filteredEquipmentMatches.length === 0 ? 0 : (safeEquipmentPage - 1) * equipmentPageSize + 1;
  const equipmentRangeEnd = Math.min(safeEquipmentPage * equipmentPageSize, filteredEquipmentMatches.length);
  const visibleEquipmentMatches = filteredEquipmentMatches.slice(equipmentRangeStart - 1, equipmentRangeEnd);


  const matchedRows = matchedList.map((m, index) => ({ ...m, id: `${m.RepoDocBookingId}-${m.NavieraBookingId}-${index}` }));
  const pendingRows = pendingList.map((m, index) => ({ ...m, id: `${m.RepoDocBookingId}-${m.NavieraBookingId}-${index}` }));
  const unmatchedRows = unmatchedList.map((b, index) => ({ ...b, id: b.BookingId || index }));

  const activeRows = tab === 0 ? matchedRows : tab === 1 ? pendingRows : unmatchedRows;
  const activeColumns = tab === 2 ? unmatchedColumns : matchColumns;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Stat cards */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexShrink: 0 }}>
        <StatCard icon={<CheckCircleOutlined fontSize="small" />} label="Conciliados" value={matched} sublabel="Match automático" color="#059669" />
        <StatCard icon={<WarningAmberOutlined fontSize="small" />} label="Pend. revisión" value={pending} sublabel="Múltiples candidatos" color="#d97706" />
        <StatCard icon={<LinkOffOutlined fontSize="small" />} label="Sin match" value={unmatched} sublabel="Sin naviera equivalente" color="#dc2626" />
      </Box>

      {/* Progress bar */}
      {total > 0 && (
        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={500}>Cobertura de conciliación</Typography>
            <Typography variant="caption" fontWeight={700} color={pct === 100 ? "#059669" : "text.primary"}>
              {pct}% de {total} bookings
            </Typography>
          </Box>
          <Box sx={{ height: 10, borderRadius: 5, bgcolor: "action.hover", overflow: "hidden", display: "flex" }}>
            {matched > 0 && <Box sx={{ height: "100%", width: `${(matched / total) * 100}%`, bgcolor: "#059669", transition: "width 0.3s" }} />}
            {pending > 0 && <Box sx={{ height: "100%", width: `${(pending / total) * 100}%`, bgcolor: "#d97706", transition: "width 0.3s" }} />}
            {unmatched > 0 && <Box sx={{ height: "100%", width: `${(unmatched / total) * 100}%`, bgcolor: "#dc2626", transition: "width 0.3s" }} />}
          </Box>
        </Box>
      )}

      {/* Alertas */}
      <Box sx={{ flexShrink: 0 }}>
        {matched === total && total > 0 && (
          <Alert severity="success" icon={<CheckCircleOutlined fontSize="small" />} sx={{ py: 0.5, mb: 1.5, borderRadius: "6px" }}>
            <Typography variant="caption" fontWeight={600}>Todos los bookings conciliados correctamente.</Typography>
          </Alert>
        )}
        {pending > 0 && (
          <Alert severity="warning" icon={<InfoOutlined fontSize="small" />} sx={{ py: 0.5, mb: 1, borderRadius: "6px" }}>
            <Typography variant="caption" fontWeight={600} display="block">{pending} booking{pending > 1 ? "s" : ""} con múltiples candidatos</Typography>
            <Typography variant="caption" color="text.secondary">Ir a Bookings → revisar y vincular manualmente.</Typography>
          </Alert>
        )}
        {unmatched > 0 && (
          <Alert severity="error" icon={<LinkOffOutlined fontSize="small" />} sx={{ py: 0.5, mb: 1.5, borderRadius: "6px" }}>
            <Typography variant="caption" fontWeight={600} display="block">{unmatched} booking{unmatched > 1 ? "s" : ""} sin match</Typography>
            <Typography variant="caption" color="text.secondary">Verificar MBL, puertos y fechas.</Typography>
          </Alert>
        )}
      </Box>

      {matchesWithEquipmentLinks.length > 0 && (
        <Box
          sx={{
            flexShrink: 0,
            mb: 2,
            p: 1.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "background.paper",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={1.5}
            sx={{ mb: 1.25 }}
          >
            <Box>
              <Typography variant="subtitle2" fontWeight={800}>
                Pares equipment/container del job
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Mostrando {equipmentRangeStart}-{equipmentRangeEnd} de {filteredEquipmentMatches.length}
                {filteredEquipmentMatches.length !== matchesWithEquipmentLinks.length ? ` filtrados (${matchesWithEquipmentLinks.length} total)` : ""}
              </Typography>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
              <TextField
                size="small"
                value={equipmentQuery}
                onChange={(event) => {
                  setEquipmentQuery(event.target.value);
                  setEquipmentPage(1);
                }}
                placeholder="Filtrar booking, container o equipment"
                sx={{ minWidth: { xs: "100%", sm: 300 }, "& .MuiOutlinedInput-root": { height: 34, fontSize: "0.78rem" } }}
              />
              <TextField
                select
                size="small"
                label="Por pag."
                value={equipmentPageSize}
                onChange={(event) => {
                  setEquipmentPageSize(Number(event.target.value));
                  setEquipmentPage(1);
                }}
                sx={{ width: 104, "& .MuiOutlinedInput-root": { height: 34, fontSize: "0.78rem" } }}
              >
                {[10, 25, 50, 100].map((size) => (
                  <MenuItem key={size} value={size}>{size}</MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>

          {filteredEquipmentMatches.length === 0 ? (
            <Alert severity="info" sx={{ py: 0.75, borderRadius: 1.5 }}>
              <Typography variant="caption" fontWeight={700} display="block">
                No hay pares que coincidan con el filtro.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Proba con booking, container, codigo de equipment o ID de equipment.
              </Typography>
            </Alert>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxHeight: 360, overflowY: "auto", pr: 0.5 }}>
              {visibleEquipmentMatches.map((match) => (
                <Box key={`${match.RepoDocBookingId}-${match.NavieraBookingId}-equipment`}>
                  <EquipmentLinksSummary
                    title={`${match.RepoDocBookingReference ?? `#${match.RepoDocBookingId}`} <-> ${match.NavieraBookingReference ?? `#${match.NavieraBookingId}`}`}
                    compact
                    context={{
                      navieraBookingId: match.NavieraBookingId,
                      navieraBookingReference: match.NavieraBookingReference,
                      repoDocBookingId: match.RepoDocBookingId,
                      repoDocBookingReference: match.RepoDocBookingReference,
                    }}
                    links={getMatchEquipmentLinks(match)}
                  />
                </Box>
              ))}
            </Box>
          )}

          {filteredEquipmentMatches.length > equipmentPageSize && (
            <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 1.25 }}>
              <Typography variant="caption" color="text.secondary">
                Pagina {safeEquipmentPage} de {equipmentPageCount}
              </Typography>
              <Pagination
                size="small"
                page={safeEquipmentPage}
                count={equipmentPageCount}
                onChange={(_, page) => setEquipmentPage(page)}
                siblingCount={1}
                boundaryCount={1}
              />
            </Stack>
          )}
        </Box>
      )}

      {/* Tabs + DataGridPro */}
      {(matchedList.length > 0 || pendingList.length > 0 || unmatchedList.length > 0) && (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ minHeight: 34, mb: 1, flexShrink: 0 }}
            TabIndicatorProps={{ style: { height: 2 } }}
          >
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CheckCircleOutlined sx={{ fontSize: 13, color: "#059669" }} />
                  Conciliados
                  <Chip label={matchedList.length} size="small" color="success" sx={{ height: 17, fontSize: "0.62rem", ...CHIP_GEOMETRIC_SX }} />
                </Box>
              }
              sx={{ minHeight: 34, textTransform: "none", fontSize: "0.78rem", py: 0 }}
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <WarningAmberOutlined sx={{ fontSize: 13, color: "#d97706" }} />
                  Revisión
                  <Chip label={pendingList.length} size="small" color="warning" sx={{ height: 17, fontSize: "0.62rem", ...CHIP_GEOMETRIC_SX }} />
                </Box>
              }
              sx={{ minHeight: 34, textTransform: "none", fontSize: "0.78rem", py: 0 }}
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <LinkOffOutlined sx={{ fontSize: 13, color: "#dc2626" }} />
                  Sin match
                  <Chip label={unmatchedList.length} size="small" color="error" sx={{ height: 17, fontSize: "0.62rem", ...CHIP_GEOMETRIC_SX }} />
                </Box>
              }
              sx={{ minHeight: 34, textTransform: "none", fontSize: "0.78rem", py: 0 }}
            />
          </Tabs>

          <Box sx={{ flex: 1, minHeight: 0 }}>
            <DataGridPro
              rows={activeRows}
              columns={activeColumns as GridColDef[]}
              density="compact"
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
              pagination
              disableColumnMenu={false}
              slots={{ toolbar: QuickFilterToolbar }}
              sx={{
                border: 0,
                height: "100%",
                "& .MuiDataGrid-cell:focus": { outline: "none" },
                "& .MuiDataGrid-row:nth-of-type(even)": { bgcolor: "rgba(0,0,0,0.015)" },
                borderRadius: "8px",
                overflow: "hidden",
              }}
              localeText={{ noRowsLabel: "Sin datos" }}
            />
          </Box>
        </Box>
      )}

      {(read<string>(result, "Message", "message")) && (
        <Box sx={{ mt: 1.5, flexShrink: 0 }}>
          <Chip
            label={read<string>(result, "Message", "message")}
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.72rem", height: "auto", py: 0.5, "& .MuiChip-label": { whiteSpace: "normal" } }}
          />
        </Box>
      )}
    </Box>
  );
}
